import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { STAGE_FLOW, STAGE_LABELS, money, shortDate, daysUntil, programName, teamColorStyle } from './constants';
import { CrmSettingsContext } from './CrmLayout';
import './Screens.css';

// Every live season for the year, grouped by stage. The board is the funnel view a
// CRM is expected to have -- but the rows underneath are seasons, not one-off deals.
//
// Deliberately no sport filter. A board that hides rows is a board you stop trusting:
// a filtered column still shows a stage heading and a count, so a deal sitting in
// Quoting looks handled when it is only hidden. The whole point of the board is that
// everything in play is on it at a glance. Search stays -- you type it, you see it,
// and it is obvious you are looking at a subset.

const Pipeline = () => {
  const navigate = useNavigate();
  const { settings } = useContext(CrmSettingsContext);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await api.get(`/crm/pipeline?${params.toString()}`);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the pipeline');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchPipeline, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchPipeline, search]);

  if (error) return <div className="crm-screen"><p className="banner error">{error}</p></div>;
  if (!data) return <div className="crm-screen"><p className="loading">Loading…</p></div>;

  const seasons = data.seasons || [];
  const byStage = STAGE_FLOW.map(stage => {
    const rows = seasons.filter(s => s.stage === stage);
    const value = rows.reduce(
      (sum, r) => sum + Number(r.actual_value || r.quoted_value || r.estimated_value || 0), 0
    );
    return { stage, rows, value };
  });

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div>
          <h1>Pipeline</h1>
          <p className="subtitle">
            {seasons.length} {seasons.length === 1 ? 'program' : 'programs'} in play
          </p>
        </div>
        <span className="season-context">Now selling the {data.year} season</span>
      </div>

      <div className="filters">
        <div className="grow">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account or mascot…"
          />
        </div>
      </div>

      {loading && <p className="loading">Loading…</p>}

      {seasons.length === 0 && !loading ? (
        <div className="card">
          <p className="empty">
            <strong>Nothing in the pipeline for {data.year}</strong>
            Seasons appear here as you add programs to accounts and start their year.
          </p>
        </div>
      ) : (
        <div className="pipeline-board">
          {byStage.map(({ stage, rows, value }) => (
            <div key={stage} className={`pipeline-col col-${stage}`}>
              <header>
                <div className="col-title">
                  <span>{STAGE_LABELS[stage]}</span>
                  <span className="col-count">{rows.length}</span>
                </div>
                <div className="col-value">{money(value)}</div>
              </header>
              <div className="col-body">
                {rows.length === 0 ? (
                  <p className="col-empty">—</p>
                ) : rows.map(season => {
                  const days = daysUntil(season.window_end);
                  const late = days !== null && days < 0 && !['ordered', 'delivered', 'lost', 'skipped'].includes(stage);
                  const colors = settings.use_team_colors ? teamColorStyle(season) : null;
                  return (
                    <button
                      key={season.id}
                      type="button"
                      className="pipeline-card"
                      onClick={() => navigate(`/crm/organizations/${season.organization_id}`)}
                    >
                      <div className="card-org">
                        {colors && <span className="team-chip" style={colors} aria-hidden="true" />}
                        {season.organization_name}
                      </div>
                      <div className="card-prog">{programName(season)}</div>
                      <div className="card-foot">
                        <span className="card-value">
                          {money(season.actual_value || season.quoted_value || season.estimated_value || 0)}
                        </span>
                        <span className={late ? 'card-late' : ''}>
                          {late ? `${Math.abs(days)}d past` : shortDate(season.window_end)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Pipeline;
